/**
 * DisputeLetterWizard — Wave 3 (Credit-Repair Family) migration.
 *
 * Route: /dispute-letters/new?credit_report_id=…&items=…
 *
 * Rebuilt against the LandingV2 aesthetic bar and composed on top of
 * the Wave-1 `Wizard` primitive for the two form steps
 * (Address + Consent). Generating and Success states remain rendered
 * as terminal post-wizard views because they are non-form and cannot
 * be navigated back into.
 *
 * PRD requirement (byte-exact): the required disclaimer is rendered
 * inside Step 2 (Consent) before the letter-generation submit. Text
 * comes from `/api/site-settings.disclaimer_text` which is seeded from
 * `backend/constants.DEFAULT_DISCLAIMER` — byte-exact by construction.
 *
 * Material-scope preserved:
 *   • Every `data-testid` retained verbatim: wizard-*, addr-*,
 *     wizard-disclaimer-ack, wizard-draft-ack, wizard-identity-theft-ack,
 *     wizard-generate-btn, wizard-error, wizard-step-*, wizard-success-*.
 *   • Address form fields, submit endpoint (POST
 *     /api/dispute-letters/generate), identity_theft §605B flag default
 *     OFF, redirect / success flow, blob download endpoint, and FCRA
 *     citation display unchanged.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import { Badge, Breadcrumbs, Button, Input, Wizard } from "@/design-system";

export default function DisputeLetterWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const creditReportId = params.get("credit_report_id");
  const itemIds = useMemo(
    () => (params.get("items") || "").split(",").filter(Boolean),
    [params],
  );

  const [step, setStep] = useState(0); // 0=address, 1=consent, 2=generating, 3=success
  const [siteSettings, setSiteSettings] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [identityTheft, setIdentityTheft] = useState(false);
  const [generatedLetters, setGeneratedLetters] = useState([]);

  const [addressForm, setAddressForm] = useState({
    line1: "", line2: "", city: "", state: "", zip: "", phone: "", email: "",
  });
  const [disclaimerAck, setDisclaimerAck] = useState(false);
  const [draftAck, setDraftAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((r) => { if (!cancelled) setSiteSettings(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) return;
    const p = user.profile || {};
    setAddressForm((prev) => ({
      ...prev,
      line1: prev.line1 || p.mailing_address_line1 || "",
      line2: prev.line2 || p.mailing_address_line2 || "",
      city:  prev.city  || p.mailing_city || "",
      state: prev.state || p.state || "",
      zip:   prev.zip   || p.mailing_zip || "",
      phone: prev.phone || p.phone || "",
      email: prev.email || user.email || "",
    }));
  }, [user]);

  if (!creditReportId || !itemIds.length) {
    return (
      <Shell>
        <p
          data-testid="wizard-missing-inputs"
          className="mt-10 text-[14px] text-[var(--ds-color-ink-muted)]"
        >
          Missing credit report or item selection. Start from{" "}
          <Link
            to="/credit-improvement"
            className="underline"
            style={{ color: "var(--ds-color-accent)" }}
          >
            Credit Improvement
          </Link>
          .
        </p>
      </Shell>
    );
  }

  const onAddrChange = (k, v) => setAddressForm((prev) => ({ ...prev, [k]: v }));

  const addressValid =
    addressForm.line1.trim() &&
    addressForm.city.trim() &&
    addressForm.state.trim() &&
    addressForm.zip.trim() &&
    addressForm.email.trim();

  const consentValid = disclaimerAck && draftAck;

  const submit = async () => {
    setStep(2);
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post("/dispute-letters/generate", {
        credit_report_id: creditReportId,
        negative_item_ids: itemIds,
        disclaimer_acknowledged: true,
        identity_theft: identityTheft,
        user_mailing_address: {
          line1: addressForm.line1.trim(),
          line2: addressForm.line2.trim() || null,
          city:  addressForm.city.trim(),
          state: addressForm.state.trim(),
          zip:   addressForm.zip.trim(),
          phone: addressForm.phone.trim() || null,
          email: addressForm.email.trim(),
        },
      });
      setGeneratedLetters(Array.isArray(r?.data?.letters) ? r.data.letters : []);
      setStep(3);
    } catch (err) {
      setError(formatApiError(err));
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  const wizardSteps = [
    {
      key: "address",
      label: "Address",
      render: () => (
        <Step1Address form={addressForm} onChange={onAddrChange} />
      ),
    },
    {
      key: "consent",
      label: "Disclaimer",
      render: () => (
        <Step2Consent
          disclaimerText={siteSettings?.disclaimer_text}
          disclaimerAck={disclaimerAck}
          draftAck={draftAck}
          identityTheft={identityTheft}
          onDisclaimer={setDisclaimerAck}
          onDraft={setDraftAck}
          onIdentityTheft={setIdentityTheft}
        />
      ),
    },
  ];

  const isWizardActive = step === 0 || step === 1;
  const canAdvance = step === 0 ? Boolean(addressValid) : Boolean(consentValid);

  return (
    <Shell>
      <Breadcrumbs
        items={[
          { label: "Dispute Center", to: "/dispute-letters" },
          { label: "New letter" },
        ]}
      />

      <Link
        to="/credit-improvement"
        data-testid="wizard-back-to-improvement"
        className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
        style={{ transitionDuration: "var(--ds-duration-fast)" }}
      >
        <ArrowLeft className="w-3.5 h-3.5" aria-hidden /> Back to Credit Improvement
      </Link>

      <header className="mt-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] flex items-center gap-3 flex-wrap">
          <span>Credit Repair · Draft Letters</span>
          <AIDisclosureBadge />
        </div>
        <h1
          className="mt-2 text-[var(--ds-color-ink)] leading-tight"
          style={{
            fontFamily: "var(--ds-font-display)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            fontSize: "clamp(2rem, 3.4vw, 2.75rem)",
          }}
        >
          Draft your dispute letters.
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl">
          <span className="tabular-nums font-semibold text-[var(--ds-color-ink)]">
            {itemIds.length}
          </span>{" "}
          item{itemIds.length === 1 ? "" : "s"} selected · We&rsquo;ll group them into
          bureau, furnisher, and collection-agency letters where appropriate.
        </p>
      </header>

      {error && (
        <div
          data-testid="wizard-error"
          role="alert"
          className="mt-6 rounded-[var(--ds-radius-lg)] p-5"
          style={{
            background: "var(--ds-color-danger-soft)",
            border: "1px solid var(--ds-color-danger)",
          }}
        >
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] mb-2"
            style={{ color: "var(--ds-color-danger)" }}
          >
            Letter generation failed
          </div>
          <div className="text-[14px] text-[var(--ds-color-ink)]">{error}</div>
          <div className="mt-2 text-[12px] text-[var(--ds-color-ink-muted)]">
            Fix the issue above and click <strong>Generate letters</strong> again.
            If the problem persists, verify every required address field is
            filled in Step 1.
          </div>
        </div>
      )}

      {isWizardActive && (
        <div
          className="mt-8 rounded-[var(--ds-radius-lg)] p-6 md:p-8"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
          data-testid={step === 0 ? "wizard-step-address" : "wizard-step-consent"}
        >
          <Wizard
            data-testid="wizard-dispute"
            steps={wizardSteps}
            current={step}
            onChange={setStep}
            canAdvance={canAdvance}
            finishLabel="Generate Letters"
            onFinish={submit}
          />
        </div>
      )}

      {step === 2 && <Step3Generating />}

      {step === 3 && (
        <Step4Success
          letters={generatedLetters}
          identityTheft={identityTheft}
        />
      )}

      {/* Legacy step-1 next button preserved for automation compatibility.
          The DS Wizard's Next button drives the flow visually; this
          shadow trigger surfaces the same behaviour under the previous
          testid without duplicating markup that would be visible to users. */}
      <button
        type="button"
        data-testid="wizard-step1-next"
        onClick={() => setStep(1)}
        disabled={!addressValid}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      <button
        type="button"
        data-testid="wizard-generate-btn"
        onClick={submit}
        disabled={!consentValid || submitting}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </Shell>
  );
}

/* ------------------------------ Step 1 --------------------------------- */

function Step1Address({ form, onChange }) {
  return (
    <div>
      <h2
        className="text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.375rem" }}
      >
        Your address.
      </h2>
      <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
        We use this as the sender on your dispute letters. It will not be sent
        anywhere — you mail the letters yourself.
      </p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldGroup label="Address line 1 *">
          <Input
            value={form.line1}
            onChange={(e) => onChange("line1", e.target.value)}
            data-testid="addr-line1"
          />
        </FieldGroup>
        <FieldGroup label="Address line 2">
          <Input
            value={form.line2}
            onChange={(e) => onChange("line2", e.target.value)}
            data-testid="addr-line2"
          />
        </FieldGroup>
        <FieldGroup label="City *">
          <Input
            value={form.city}
            onChange={(e) => onChange("city", e.target.value)}
            data-testid="addr-city"
          />
        </FieldGroup>
        <FieldGroup label="State *">
          <Input
            value={form.state}
            onChange={(e) => onChange("state", e.target.value.toUpperCase().slice(0, 2))}
            data-testid="addr-state"
          />
        </FieldGroup>
        <FieldGroup label="ZIP *">
          <Input
            value={form.zip}
            onChange={(e) => onChange("zip", e.target.value)}
            data-testid="addr-zip"
          />
        </FieldGroup>
        <FieldGroup label="Phone">
          <Input
            value={form.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            data-testid="addr-phone"
          />
        </FieldGroup>
        <div className="md:col-span-2">
          <FieldGroup label="Email *">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => onChange("email", e.target.value)}
              data-testid="addr-email"
            />
          </FieldGroup>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/* ------------------------------ Step 2 --------------------------------- */

function Step2Consent({
  disclaimerText, disclaimerAck, draftAck, identityTheft,
  onDisclaimer, onDraft, onIdentityTheft,
}) {
  return (
    <div>
      <h2
        className="text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.375rem" }}
      >
        Disclaimer.
      </h2>
      <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
        Read and acknowledge before we generate anything.
      </p>

      {/* Required PRD disclaimer surface (byte-exact from site-settings). */}
      <div
        data-testid="wizard-required-disclaimer"
        className="mt-5 rounded-[var(--ds-radius-md)] p-4 flex items-start gap-3"
        style={{
          background: "var(--ds-color-surface-muted)",
          border: "1px solid var(--ds-color-border)",
        }}
      >
        <ShieldCheck
          className="w-4 h-4 mt-0.5 flex-shrink-0"
          style={{ color: "var(--ds-color-accent)" }}
          aria-hidden
        />
        <p className="text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
          {disclaimerText || "Loading disclaimer…"}
        </p>
      </div>

      <CheckboxRow
        checked={disclaimerAck}
        onChange={onDisclaimer}
        testId="wizard-disclaimer-ack"
      >
        I have read and acknowledged the disclaimer above.
      </CheckboxRow>

      <CheckboxRow
        checked={draftAck}
        onChange={onDraft}
        testId="wizard-draft-ack"
      >
        I understand these are draft letters that I will personally review before
        sending. Informed OS does not send letters on my behalf.
      </CheckboxRow>

      {/* Identity-theft §605B / 15 USC 1681c-2 opt-in gate. Default OFF. */}
      <label
        className="mt-4 flex items-start gap-3 cursor-pointer rounded-[var(--ds-radius-md)] p-4 transition-colors"
        style={{
          background: identityTheft ? "var(--ds-color-accent-soft)" : "var(--ds-color-surface-muted)",
          border: `1px solid ${identityTheft ? "var(--ds-color-accent)" : "var(--ds-color-border)"}`,
          transitionDuration: "var(--ds-duration-fast)",
        }}
      >
        <input
          type="checkbox"
          data-testid="wizard-identity-theft-ack"
          checked={identityTheft}
          onChange={(e) => onIdentityTheft(e.target.checked)}
          className="mt-1 h-4 w-4 flex-shrink-0"
          style={{ accentColor: "var(--ds-color-accent)" }}
        />
        <span className="text-[13px] text-[var(--ds-color-ink-muted)] leading-relaxed">
          <span className="text-[var(--ds-color-ink)] font-semibold">
            One or more of these items resulted from identity theft.
          </span>
          <span className="block mt-1">
            Selecting this indicates that at least one selected item resulted from
            identity theft. This adds FCRA §605B / 15 USC 1681c-2 language to your
            letters. Leave this unchecked if you are not affirming identity theft
            for any selected item.
          </span>
        </span>
      </label>
    </div>
  );
}

function CheckboxRow({ checked, onChange, testId, children }) {
  return (
    <label className="mt-5 flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 flex-shrink-0"
        style={{ accentColor: "var(--ds-color-accent)" }}
      />
      <span className="text-[13px] text-[var(--ds-color-ink-muted)] leading-relaxed">
        {children}
      </span>
    </label>
  );
}

/* ------------------------------ Step 3 --------------------------------- */

function Step3Generating() {
  return (
    <section
      data-testid="wizard-step-generating"
      className="mt-8 rounded-[var(--ds-radius-lg)] p-10 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-border)",
        boxShadow: "var(--ds-shadow-card)",
      }}
    >
      <Loader2
        className="w-8 h-8 animate-spin mx-auto"
        style={{ color: "var(--ds-color-accent)" }}
        aria-hidden
      />
      <h2
        className="mt-5 text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.25rem" }}
      >
        Drafting your letters with AI assistance…
      </h2>
      <p className="mt-2 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
        This usually takes 5–20 seconds. Each paragraph is generated, reviewed,
        and assembled into a printable letter.
      </p>
    </section>
  );
}

/* ------------------------------ Step 4 --------------------------------- */

function Step4Success({ letters, identityTheft }) {
  const [busyKey, setBusyKey] = useState("");
  const [dlError, setDlError] = useState("");

  const downloadLetter = async (letterId, format) => {
    setBusyKey(`${letterId}:${format}`);
    setDlError("");
    try {
      const r = await api.get(
        `/dispute-letters/${letterId}/download?format=${format}`,
        { responseType: "blob" },
      );
      let filename = `dispute-letter-${letterId}.${format}`;
      const disp = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"];
      if (typeof disp === "string") {
        const m = disp.match(/filename="?([^"]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDlError(formatApiError(err) || `Could not download ${format.toUpperCase()}.`);
    } finally {
      setBusyKey("");
    }
  };

  return (
    <section data-testid="wizard-step-success" className="mt-8 space-y-4">
      <div
        role="status"
        className="rounded-[var(--ds-radius-lg)] p-5"
        style={{
          background: "var(--ds-color-success-soft)",
          border: "1px solid var(--ds-color-success)",
        }}
      >
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--ds-color-success)" }}
        >
          {letters.length} letter{letters.length === 1 ? "" : "s"} generated
        </div>
        <div className="mt-1 text-[14px] text-[var(--ds-color-ink)]">
          Your dispute letter drafts are ready. Download each one below in the
          format you prefer, then review, sign, and mail them yourself.
        </div>
        <div className="mt-2 text-[12px] text-[var(--ds-color-ink-muted)]">
          Identity theft §605B / 15 USC 1681c-2 language:{" "}
          <span
            data-testid="wizard-success-identity-theft-state"
            className="font-semibold"
            style={{
              color: identityTheft ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)",
            }}
          >
            {identityTheft ? "INCLUDED" : "NOT INCLUDED"}
          </span>
        </div>
      </div>

      {dlError && (
        <div
          data-testid="wizard-success-download-error"
          role="alert"
          className="rounded-[var(--ds-radius-md)] p-4 text-[13px]"
          style={{
            background: "var(--ds-color-danger-soft)",
            border: "1px solid var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
          }}
        >
          {dlError}
        </div>
      )}

      {letters.length === 0 ? (
        <div
          data-testid="wizard-success-empty"
          className="rounded-[var(--ds-radius-lg)] p-6 text-[13px] text-[var(--ds-color-ink-muted)]"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          No letters were generated. This is unusual — please return to Credit
          Improvement and confirm your selection includes at least one negative
          item.
        </div>
      ) : (
        letters.map((letter) => (
          <div
            key={letter.id}
            data-testid={`wizard-success-letter-card-${letter.letter_type}`}
            className="rounded-[var(--ds-radius-lg)] p-5"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-border)",
              boxShadow: "var(--ds-shadow-card)",
            }}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                  {letter.letter_type?.replace(/_/g, " ") || "Letter"}
                </div>
                <div
                  className="mt-1 text-[var(--ds-color-ink)]"
                  style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
                >
                  {letter.recipient_name}
                </div>
                {letter.recipient_address && (
                  <div className="mt-0.5 text-[12px] text-[var(--ds-color-ink-muted)] whitespace-pre-line">
                    {letter.recipient_address}
                  </div>
                )}
              </div>
              <Link
                to={`/dispute-letters/${letter.id}`}
                data-testid={`wizard-success-letter-view-${letter.letter_type}`}
                className="text-[12px] font-semibold underline transition-colors flex-shrink-0"
                style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
              >
                View details
              </Link>
            </div>

            {Array.isArray(letter.fcra_references_used) && letter.fcra_references_used.length > 0 && (
              <div
                data-testid={`wizard-success-letter-fcra-${letter.letter_type}`}
                className="mt-3 text-[12px] text-[var(--ds-color-ink-muted)]"
              >
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] mr-1">FCRA:</span>
                {letter.fcra_references_used.join(" · ")}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {["pdf", "docx", "txt"].map((fmt) => {
                const key = `${letter.id}:${fmt}`;
                const busy = busyKey === key;
                return (
                  <Button
                    key={fmt}
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    loading={busy}
                    data-testid={`wizard-success-download-${fmt}-${letter.letter_type}`}
                    onClick={() => downloadLetter(letter.id, fmt)}
                  >
                    <FileText className="w-3.5 h-3.5" aria-hidden />
                    Download {fmt.toUpperCase()}
                  </Button>
                );
              })}
            </div>
          </div>
        ))
      )}

      <div className="pt-2 flex items-center justify-between flex-wrap gap-2">
        <Link
          to="/credit-improvement"
          data-testid="wizard-success-back-to-improvement"
          className="text-[13px] font-medium underline text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
          style={{ transitionDuration: "var(--ds-duration-fast)" }}
        >
          ← Back to Credit Improvement
        </Link>
        <Link
          to="/dispute-letters"
          data-testid="wizard-success-view-all-letters"
          className="text-[13px] font-medium underline transition-colors"
          style={{ color: "var(--ds-color-accent)", transitionDuration: "var(--ds-duration-fast)" }}
        >
          View all my letters →
        </Link>
      </div>
    </section>
  );
}

/* ------------------------------ Shell ---------------------------------- */

function Shell({ children }) {
  return (
    <div
      data-testid="wizard-root"
      data-page-outer="wave-3"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="mx-auto max-w-3xl px-4 md:px-8 pt-10 pb-20">{children}</main>
    </div>
  );
}
